import { Injectable, inject } from '@angular/core';
import { MessageService } from 'primeng/api';
import { EMPTY, Subscription, timer } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { Announcement } from '../models/announcement';
import { AnnouncementManagementService } from './announcement-management.service';
import { ILogger, LOGGER_TOKEN } from './logger/ilogger';

const ANNOUNCEMENT_TOAST_KEY = 'announcements';
const ANNOUNCEMENT_POLL_INTERVAL_MS = 60_000;

@Injectable({
  providedIn: 'root'
})
export class AnnouncementToastService {
  private readonly announcementService = inject(AnnouncementManagementService);
  private readonly messageService = inject(MessageService);
  private readonly logger = inject<ILogger>(LOGGER_TOKEN);
  private readonly displayedAnnouncementIds = new Set<string>();
  private pollSubscription?: Subscription;

  start(): void {
    if (this.pollSubscription) {
      return;
    }

    this.pollSubscription = timer(0, ANNOUNCEMENT_POLL_INTERVAL_MS)
      .pipe(
        switchMap(() => this.announcementService.getActiveAnnouncements().pipe(
          catchError((error) => {
            this.logger.exception(error);
            return EMPTY;
          })
        ))
      )
      .subscribe((announcements) => {
        this.removeResolvedAnnouncements(announcements);
        announcements.forEach((announcement) => this.displayAnnouncement(announcement));
      });
  }

  stop(): void {
    this.pollSubscription?.unsubscribe();
    this.pollSubscription = undefined;
    this.displayedAnnouncementIds.clear();
    this.messageService.clear(ANNOUNCEMENT_TOAST_KEY);
  }

  dismissAnnouncement(announcementId: string | undefined): void {
    if (!announcementId) {
      return;
    }

    this.announcementService.acknowledgeAnnouncement(announcementId).subscribe({
      next: () => undefined,
      error: (error) => {
        this.logger.exception(error);
      }
    });
  }

  private displayAnnouncement(announcement: Announcement): void {
    if (!announcement._id || this.displayedAnnouncementIds.has(announcement._id)) {
      return;
    }

    this.displayedAnnouncementIds.add(announcement._id);
    this.messageService.add({
      key: ANNOUNCEMENT_TOAST_KEY,
      id: `announcement-${announcement._id}`,
      severity: announcement.severity ?? (announcement.source === 'ingest' ? 'warn' : 'info'),
      icon: announcement.source === 'ingest' ? undefined : 'pi pi-info-circle',
      summary: announcement.notification_name,
      detail: announcement.description,
      sticky: true,
      closable: true,
      data: announcement
    });
  }

  private removeResolvedAnnouncements(announcements: Announcement[]): void {
    const activeIds = new Set(
      announcements
        .map((announcement) => announcement._id)
        .filter((id): id is string => Boolean(id))
    );
    const hasResolvedAnnouncements = [...this.displayedAnnouncementIds]
      .some((id) => !activeIds.has(id));

    if (!hasResolvedAnnouncements) {
      return;
    }

    this.messageService.clear(ANNOUNCEMENT_TOAST_KEY);
    this.displayedAnnouncementIds.clear();
    announcements.forEach((announcement) => this.displayAnnouncement(announcement));
  }
}
